<template>
	<div class="chat-search-panel content-block">
		<div class="search-row flex align-items-center">
			<app-tooltip
				class="chat-search-panel__add-btn"
				:text="$t('chat_create_dialog')"
			>
				<template #activator>
					<app-icon
						name="plus"
						width="20px"
						color="#979797"
						hover-color="#499259"
						cursor="pointer"
						@click="onCreateChat"
					/>
				</template>
			</app-tooltip>
			<div class="search-input-container flex align-items-center">
				<app-input
					v-model="searchText"
					class="search-input"
					:placeholder="$t('search_label')"
				/>
			</div>
			<app-tooltip
				class="chat-search-panel__edit-btn"
				:text="$t('chat_edit_dialog')"
			>
				<template #activator>
					<app-icon
						v-if="
							openedChat
							&& !openedChat.IsSystem
							&& openedChat.CanEdit
						"
						name="settings-outline"
						width="18px"
						color="#979797"
						hover-color="#499259"
						cursor="pointer"
						@click="onEditChat"
					/>
				</template>
			</app-tooltip>
		</div>
		<div
			v-if="chatsTabs.length"
			class="chat-search-panel__chats-tabs"
		>
			<div class="chat-search-panel__chats-tabs-back">
				<app-icon
					class="chat-search-panel__chats-tabs-back-btn"
					name="arrow-return"
					width="20px"
					color="#979797"
					hover-color="#499259"
					cursor="pointer"
					@click="goBack"
				/>
			</div>
			<div class="chat-search-panel__chats-tabs-items">
				<app-tooltip
					v-for="chat in chatsTabs"
					:text="$t('chat_go_to_dialog')"
					:disabled="hoveredCloseTabId === chat.Id"
					:key="chat.Id"
				>
					<template #activator>
						<div
							:class="{
								'chat-search-panel__chats-tabs-item': true,
								'chat-search-panel__chats-tabs-item--active': openedChatId === chat.Id,
							}"
							:key="chat.Id"
							@click="onOpenDialog(chat.Id)"
						>
							{{ chat.Name }}{{ getUnreadCount(chat.Id) > 0 ? ` (${getUnreadCount(chat.Id)})` : '' }}
							<app-tooltip
								:text="$t('close_label')"
								@mouseenter="hoveredCloseTabId = chat.Id"
								@mouseleave="hoveredCloseTabId = null"
							>
								<template #activator>
									<app-icon
										name="close-bold"
										width="12px"
										:color="openedChatId === chat.Id ? '#FFFFFF' : '#D2D2D2'"
										hover-color="#BA4C4C"
										cursor="pointer"
										@click.stop="onDeleteTab(chat.Id)"
									/>
								</template>
							</app-tooltip>
						</div>
					</template>
				</app-tooltip>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	defineEmits,
	ref,
	watch
} from "vue";
import { headerStore } from "@/store/header";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

const props = defineProps<{
	modelValue: string;
}>()
const emit = defineEmits(['update:modelValue', 'request-focus']);

const store = headerStore()

const searchText = ref("");
const hoveredCloseTabId = ref<number | null>(null);

const chatsTabs = computed(() => store.getOpenedChatsHistory)
const openedChatId = computed(() => store.getOpenedChatId)
const openedChat = computed(() => store.getOpenedChat)

const getUnreadCount = (chatId: number): number =>
	store.dialogs.find(d => d.Id === chatId)?.UnreadCount ?? 0

const goBack = () => {
	searchText.value = ""
	store.setMode('list')
	store.setOpenedChat(null)
}
const onOpenDialog = async (chatId: number) => {
	if (store.getOpenedChatId !== chatId) {
		emit('request-focus')
	}
	searchText.value = "";
	store.setMode('chat')
	await store.setOpenedChat(chatId)
}


const onDeleteTab = (chatId: number) => {
	if (openedChatId.value === chatId) {
		searchText.value = ""
		store.setMode('list')
		store.setOpenedChat(null)
	}
	store.deleteOpenedChatsHistory(chatId)
}

const onCreateChat = () => {
	store.setMode('create-chat')
	store.setOpenedChat(null)
}

const onEditChat = () => {
	store.setEditedChat(openedChat.value)
	store.setMode('edit-chat')
	store.setOpenedChat(null)
}

watch(searchText, (newVal) => {
	if (newVal !== props.modelValue) {
		emit('update:modelValue', searchText.value)
	}
})

watch(() => props.modelValue, (newVal) => {
	if (newVal !== searchText.value) {
		searchText.value = newVal;
	}
})
</script>

<style scoped lang="scss">
.chat-search-panel {
	&__edit-btn {
		margin-left: 4px;
	}

	&__chats-tabs {
		display: flex;
		margin-top: 8px;

		&-back {
			display: flex;
			justify-content: center;
			width: fit-content;
			height: fit-content;
			margin-right: 4px;
		}

		&-items {
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			min-height: 20px;
			gap: 5px;
		}

		&-item {
			display: flex;
			align-items: center;
			gap: 1px;
			padding: 3px 6px;
			border-radius: 6px;
			color: #979797;
			font-size: 12px;
			line-height: 16px;
			font-weight: 500;
			box-shadow: 0 0 1px 0.5px #D2D2D2 inset;

			transition: background-color 0.1s ease-in;
			cursor: pointer;

			&:hover {
				background-color: #EEEEEE;
			}

			&--active {
				background-color: #6DA279 !important;
				color: #FFFFFF;
				font-weight: 600;
			}

			/* Обёртки тултипа крестика не должны ломать выравнивание иконки по центру */
			&::v-deep(.app-tooltip),
			&::v-deep(.app-tooltip__activator) {
				display: flex;
				align-items: center;
			}
		}
	}

	&::v-deep(.q-field__control) {
		height: 32px;
	}
}
.content-block {
	display: flex;
	flex-direction: column;
	background: #fff;
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	padding: 8px;
	flex-shrink: 0;
	border-bottom: 1px solid #F2F2F2;
}

.search-row {
	display: flex;
	align-items: center;
	gap: 4px;
}

.add-btn {
	color: gray;
	min-width: 32px;
}

.search-input-container {
	flex: 1;
	position: relative;
}

.search-input {
	width: 100%;

	&__icon {
		margin-top: -8px;
	}
}

.flex {
	display: flex;
	align-items: center;
}
</style>
